import Cookies from 'js-cookie';

export const PERMISSION_MODE = Object.freeze({
  NONE: 'none',
  VIEW: 'view',
  EDIT: 'edit',
});

export const PERMISSION_ACTION = Object.freeze({
  ADD: 'add',
  EDIT: 'edit',
  DELETE: 'delete',
});

const STORAGE_KEY = 'customPagePermission';
const COOKIE_KEY = 'isCustomPageAccess';

// Child-to-parent map — keeps parent's mode for detail/child routes.
// ใช้ชุดเดียวกับ router.js (import ไปเป็น childToParent) ทั้งตอนตรวจสิทธิ์แบบ custom page
// และตอนไล่สายเมนูที่ถูกปิดตาม menuClosure config
// หน้า detail ต้องชี้ไปหาหน้า list ที่มีในรายการ Page Access ไม่ใช่หัวข้อกลุ่ม เพราะ key กลุ่ม
// (เช่น 'TaxPlanningV2') ไม่เคยถูกบันทึกลงสิทธิ์ ถ้าชี้กลุ่ม ผู้ที่ได้สิทธิ์หน้า list จะเปิดหน้า detail ไม่ได้
export const CHILD_TO_PARENT = {
  GPTModelDetail: 'GPTModelManagement',
  UploadVector: 'VectorManagement',
  DocumentControl: 'VectorManagement',
  DataIntegrationDetail: 'DataIntegration',
  MutualFundDetail: 'MutualFund',
  VoiceConfigDetail: 'VoiceConfigs',
  ProductDetail: 'ProductGroup',
  UploadSoundToDocumentDetail: 'UploadSoundToDocument',
  CommunityGraph: 'CommunityManagement',
  MetaCommunityGraph: 'MetaCommunityManagement',
  FlowCreator: 'FlowList',
  FlowCompare: 'FlowList',
  IntentSession: 'IntentGroup',
  IntentSessionDetail: 'IntentGroup',
  PromptTemplate: 'GroupPromptTemplate',
  ReplaceText: 'GroupReplacetext',
  ValidationGenerateUpload: 'ValidationGenerate',
  ValidationPositiveDetail: 'ValidationPositive',
  ValidationNegativeDetail: 'ValidationNegative',
  DashboardScore: 'ValidationProcess',
  UserGroupDetail: 'GroupUserManagement',
  SetupUserGroup: 'GroupUserManagement',
  ValidationJobDetail: 'ValidationProcess',
  ValidationJobDetailV2: 'ValidationProcess',
  ValidationMasterDataV2: 'ValidationProcess',
  ValidationTestRunnerV2: 'ValidationProcess',
  ValidationDashboardV2: 'ValidationProcess',
  DocumentGroupManagement: 'VectorManagement',
  ProductGroupManagement: 'ProductGroup',
  QAOverview: 'QAConsole',
  QAAgents: 'QAConsole',
  QAAgentDetail: 'QAAgents',
  QACases: 'QAConsole',
  QACaseDetail: 'QACases',
  QAInvestigations: 'QAConsole',
  QASettings: 'QAConsole',
  QAJobs: 'QAConsole',
  TaxCategory: 'TaxPlanning',
  TaxConfiguration: 'TaxPlanning',
  TaxConfigurationEdit: 'TaxPlanning',
  TaxQuestionEdit: 'TaxPlanning',
  TaxCalculateEdit: 'TaxPlanning',
  TaxFormulaEdit: 'TaxPlanning',
  TaxSandbox: 'TaxPlanning',
  TaxHistory: 'TaxPlanning',
  TaxHistoryDetail: 'TaxPlanning',
  // หน้าในกลุ่ม Tax Planning V2 ชี้กลุ่ม เพื่อให้ isRouteClosed ไล่สายไปถึงกลุ่มได้
  TaxConfigurationV2: 'TaxPlanningV2',
  TaxPlaygroundV2: 'TaxPlanningV2',
  TaxFeedbackSettings: 'TaxPlanningV2',
  TaxLogV2: 'TaxPlanningV2',
  TaxChatLog: 'TaxPlanningV2',
  TaxHistoryV2: 'TaxPlanningV2',
  // ย้ายมาอยู่ใต้ Tax Planning V2 พร้อมกับการย้ายเมนูใน NavBar.vue
  // ถ้ายังชี้ 'TaxPlanning' ไว้ isRouteClosed จะไล่สายไปเจอกลุ่มที่ถูกปิด แล้วปิดทั้งสามหน้าตามไปด้วย
  TaxDashboard: 'TaxPlanningV2',
  TaxPlanningMobileConfigurations: 'TaxPlanningV2',
  TaxConsent: 'TaxPlanningV2',
  // Decrypt/Encrypt CIF มีสิทธิ์ของตัวเองใน Page Access แล้ว — ชี้กลุ่มไว้เพื่อสายเมนูปิดเท่านั้น
  DecryptTax: 'TaxPlanningV2',
  // หน้า detail ของ Tax V2 → หน้า list ที่เปิดเข้ามา (ตรงกับ DETAIL_ROUTES ใน NavBar.vue)
  TaxConfigurationV2Edit: 'TaxConfigurationV2',
  TaxQuestionV2Edit: 'TaxConfigurationV2',
  TaxCalculateV2List: 'TaxConfigurationV2',
  TaxCalcFunctionV2Edit: 'TaxConfigurationV2',
  TaxLogV2Detail: 'TaxLogV2',
  TaxLogV2Offline: 'TaxLogV2',
  TaxHistoryV2Detail: 'TaxHistoryV2',
  TaxConsentEdit: 'TaxConsent',
};

// Build a normalized actions object. Missing or invalid input falls back to all-true (legacy
// behavior: a record without explicit actions represents full access).
function normalizeActions(raw) {
  if (raw && typeof raw === 'object') {
    return {
      add: raw.add !== false,
      edit: raw.edit !== false,
      delete: raw.delete !== false,
    };
  }
  return { add: true, edit: true, delete: true };
}

// Parses the stored permission list. Tolerates:
//  - new object format `[{ key, mode, actions: { add, edit, delete } }]`
//  - intermediate object format `[{ key, mode }]` (no actions → legacy full access on edit)
//  - legacy string list `['Key']` (treated as edit mode + full access)
export function readStoredPermissions() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => {
        if (typeof entry === 'string') {
          return {
            key: entry,
            mode: PERMISSION_MODE.EDIT,
            actions: normalizeActions(null),
          };
        }
        if (entry && typeof entry === 'object' && typeof entry.key === 'string') {
          const mode = entry.mode === PERMISSION_MODE.VIEW ? PERMISSION_MODE.VIEW : PERMISSION_MODE.EDIT;
          const actions = mode === PERMISSION_MODE.VIEW
            ? { add: false, edit: false, delete: false }
            : normalizeActions(entry.actions);
          return { key: entry.key, mode, actions };
        }
        return null;
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

export function isCustomPageAccessEnabled() {
  return Cookies.get(COOKIE_KEY) === 'true';
}

// Resolves the matching permission record for a route. Detail/child routes inherit from their
// parent. Returns null when nothing applies.
function resolvePermission(routeName) {
  if (!routeName) return null;
  const list = readStoredPermissions();
  if (list.length === 0) return null;

  const direct = list.find((p) => p.key === routeName);
  if (direct) return direct;

  const parentKey = CHILD_TO_PARENT[routeName];
  if (parentKey) {
    const parent = list.find((p) => p.key === parentKey);
    if (parent) return parent;
  }
  return null;
}

// Returns the permission mode for a route name.
// When custom access is off, returns 'edit' (i.e., full access — no restriction from this system).
// When on, returns 'view' or 'edit' if listed (considering parent route for detail pages),
// or 'none' if not listed.
export function getPageMode(routeName) {
  if (!routeName) return PERMISSION_MODE.NONE;
  if (!isCustomPageAccessEnabled()) return PERMISSION_MODE.EDIT;

  const perm = resolvePermission(routeName);
  return perm ? perm.mode : PERMISSION_MODE.NONE;
}

// Returns the granular action map for a route. When custom access is off, all actions are true.
// When on, derives from the stored record (legacy edit-without-actions → full access).
export function getPageActions(routeName) {
  if (!isCustomPageAccessEnabled()) {
    return { add: true, edit: true, delete: true };
  }
  const perm = resolvePermission(routeName);
  if (!perm) return { add: false, edit: false, delete: false };
  if (perm.mode === PERMISSION_MODE.VIEW) {
    return { add: false, edit: false, delete: false };
  }
  return normalizeActions(perm.actions);
}

export function isViewOnly(routeName) {
  return getPageMode(routeName) === PERMISSION_MODE.VIEW;
}

export function canAccess(routeName) {
  const mode = getPageMode(routeName);
  return mode === PERMISSION_MODE.VIEW || mode === PERMISSION_MODE.EDIT;
}
export function canView(routeName) {
  return getPageActions(routeName).add === true;
}
export function canAdd(routeName) {
  return getPageActions(routeName).add === true;
}

export function canEdit(routeName) {
  return getPageActions(routeName).edit === true;
}

export function canDelete(routeName) {
  return getPageActions(routeName).delete === true;
}

// True when the user has any write capability (add/edit/delete) on this route.
// Useful for hiding/showing whole sections (e.g., a save button that gates any modification).
export function canModify(routeName) {
  const a = getPageActions(routeName);
  return a.add === true || a.edit === true || a.delete === true;
}

// Returns just the list of allowed route keys (for router/navbar filtering).
export function getAllowedKeys() {
  return readStoredPermissions().map((p) => p.key);
}

export default {
  PERMISSION_MODE,
  PERMISSION_ACTION,
  readStoredPermissions,
  isCustomPageAccessEnabled,
  getPageMode,
  getPageActions,
  isViewOnly,
  canAccess,
  canAdd,
  canEdit,
  canDelete,
  canModify,
  getAllowedKeys,
};
